import DeleteIcon from "@mui/icons-material/Delete";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SettingsIcon from "@mui/icons-material/Settings";
import { Box, Breadcrumbs, Divider, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { Link } from "react-router-dom";

import { FoxgloveIntegrationStepper } from "@/Integrations/FoxgloveIntegration/FoxgloveIntegrationStepper.tsx";
import { useAuth } from "@/providers";
import { FoxgloveIcon } from "@/Settings/Integrations/IntegrationIcon";
import {
  AlertDialog,
  NavigationPage,
  PageHeader,
  RobotoButton,
  RobotoTypography,
} from "@/shared/components";
import { useDeleteWebhookIntegration } from "@/shared/domain/integrations/hooks/useDeleteWebhookIntegration.ts";
import { useFoxgloveIntegration } from "@/shared/domain/integrations/hooks/useFoxgloveIntegration.ts";

import styles from "./FoxgloveIntegration.module.css";

export function FoxgloveIntegration() {
  const theme = useTheme();
  const { currentOrganization } = useAuth();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: foxgloveIntegration } = useFoxgloveIntegration(
    currentOrganization?.org_id,
  );
  const deleteIntegrationMutation = useDeleteWebhookIntegration();

  const webhookEndpointId = foxgloveIntegration?.webhook_endpoint_id;

  const handleDeleteIntegration = (): Promise<void> => {
    if (!foxgloveIntegration?.webhook_endpoint_id) {
      return Promise.resolve();
    }

    return deleteIntegrationMutation.mutateAsync({
      webhookEndpointId: foxgloveIntegration.webhook_endpoint_id,
    });
  };

  return (
    <NavigationPage title="Roboto - Foxglove Integration">
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{
          mb: 2,
          "& a": {
            color: theme.palette.primary.main,
          },
        }}
      >
        <Link to="/settings" className={styles.breadcrumbLink}>
          <SettingsIcon fontSize="small" />
          <Typography variant="body2">Settings</Typography>
        </Link>
        <Link to="/settings?tab=5" className={styles.breadcrumbLink}>
          <Typography variant="body2">Integrations</Typography>
        </Link>
        <Typography variant="body2" color="text.primary">
          Foxglove
        </Typography>
      </Breadcrumbs>

      <Divider
        sx={{
          borderColor: theme.border.color,
          mb: 3,
        }}
      />

      <Box className={styles.headerContainer}>
        <PageHeader className={styles.headerWithIcon}>
          <FoxgloveIcon size={32} />
          Foxglove Integration
        </PageHeader>

        {foxgloveIntegration && (
          <RobotoButton
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
            disabled={deleteIntegrationMutation.isPending}
            eventName="DeleteFoxgloveIntegrationClicked"
          >
            Delete Integration
          </RobotoButton>
        )}
      </Box>

      <RobotoTypography variant="body1" sx={{ mb: 3, mt: 2 }}>
        Connect your Foxglove account to automatically import and process your
        robotics data in Roboto.
      </RobotoTypography>

      <Box
        className={styles.stepperContainer}
        sx={{
          marginTop: theme.spacing(3),
        }}
      >
        <FoxgloveIntegrationStepper webhookEndpointId={webhookEndpointId} />
      </Box>

      <AlertDialog
        dialogOpen={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        dialogTitle="Delete Foxglove Integration"
        dialogText="Are you sure you want to delete this Foxglove integration? This action cannot be undone."
        dialogAction={handleDeleteIntegration}
      />
    </NavigationPage>
  );
}
